import { createElement, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { Icon } from '@lounge-fe/ui-kit'

const PlayButton = ({ video }: { video: HTMLVideoElement }) => {
  const [isPlaying, setIsPlaying] = useState(false)

  useEffect(() => {
    const play = () => setIsPlaying(true)
    play()
    const pause = () => setIsPlaying(false)
    video.addEventListener('play', play)
    video.addEventListener('pause', pause)
    return () => {
      video.removeEventListener('play', play)
      video.removeEventListener('pause', pause)
    }
  }, [video])

  const togglePlay = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (video.paused) video.play()
    else video.pause()
  }

  return (
    <button
      onClick={togglePlay}
      style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
    >
      {isPlaying ? (
        <Icon.Pause style={{ color: 'white' }} />
      ) : (
        <Icon.Play style={{ color: 'white' }} />
      )}
    </button>
  )
}

class VideoPlayButton extends HTMLElement {
  private shadow!: ShadowRoot
  private currentVideo: HTMLVideoElement | null = null
  private root: ReturnType<typeof createRoot> | null = null

  connectedCallback() {
    this.shadow = this.attachShadow({ mode: 'closed' })
    this.root = createRoot(this.shadow)

    const updateVideo = () => {
      const allVideos = Array.from(
        this.parentElement?.querySelectorAll('video') || [],
      )

      const visibleVideo = allVideos.find(
        (v) =>
          getComputedStyle(v).display !== 'none' && v.offsetParent !== null,
      )

      if (
        this.currentVideo &&
        this.currentVideo !== visibleVideo &&
        this.currentVideo.offsetParent === null
      ) {
        this.currentVideo.pause()
      }

      if (visibleVideo && visibleVideo !== this.currentVideo) {
        this.currentVideo = visibleVideo
        if (this.currentVideo.paused) {
          this.currentVideo.play().catch((err) => {
            console.warn('[video-play-btn] Autoplay blocked:', err)
          })
        }
        this.root?.render(
          createElement(PlayButton, {
            video: this.currentVideo,
            key:
              this.currentVideo.currentSrc ||
              this.currentVideo.src ||
              Math.random().toString(),
          }),
        )
      }
    }

    requestAnimationFrame(updateVideo)

    window.addEventListener('resize', updateVideo)
    this._disconnect = () => window.removeEventListener('resize', updateVideo)
  }

  disconnectedCallback() {
    this._disconnect?.()
  }

  private _disconnect?: () => void
}

customElements.define('video-play-btn', VideoPlayButton)
